import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import emailjs from '@emailjs/browser';
import './IntegrationAssessmentModal.css';
// Import the colored logo from root directory (blue and yellow/orange)
import logoImage from '../images/logo.png';

const IntegrationAssessmentModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({
    sensors: [],
    iot: [],
    remoteSensingSurveying: [],
    dataManagement: [],
    intelligence: []
  });
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    designation: '',
    phone: '',
    countryCode: '+91'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // HubSpot configuration
  const HUBSPOT_PORTAL_ID = '46653167';
  const HUBSPOT_FORM_ID = 'd773f129-8b72-488a-88fe-5eba1daa4ae6';

  // EmailJS configuration - from .env file
  // Get your credentials from https://www.emailjs.com/
  // IMPORTANT: After adding .env variables, RESTART the development server!

  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
  
  // Debug: Log if credentials are loaded (only shows if they exist, not the actual values)
  useEffect(() => {
    console.log('📧 EmailJS Configuration Check:');
    console.log('  Service ID:', EMAILJS_SERVICE_ID ? '✅ Loaded' : '❌ Missing');
    console.log('  Template ID:', EMAILJS_TEMPLATE_ID ? '✅ Loaded' : '❌ Missing');
    console.log('  Public Key:', EMAILJS_PUBLIC_KEY ? '✅ Loaded' : '❌ Missing');
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      console.warn('⚠️ EmailJS not configured. Add to .env file and RESTART server:');
      console.warn('   REACT_APP_EMAILJS_SERVICE_ID=your_service_id');
      console.warn('   REACT_APP_EMAILJS_TEMPLATE_ID=your_template_id');
      console.warn('   REACT_APP_EMAILJS_PUBLIC_KEY=your_public_key');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Country codes data
  const countryCodes = [
    { code: '+91', country: 'India', flag: '🇮🇳', phoneLength: 10 },
    { code: '+1', country: 'United States', flag: '🇺🇸', phoneLength: 10 },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧', phoneLength: 10 },
    { code: '+61', country: 'Australia', flag: '🇦🇺', phoneLength: 9 },
    { code: '+971', country: 'United Arab Emirates', flag: '🇦🇪', phoneLength: 9 }
  ];

  const steps = [
    {
      id: 'intro',
      title: 'Check your Monitoring score',
      description: 'Take this quick assessment to get your monitoring score and see how close you are to smarter, faster decisions',
      type: 'intro'
    },
    {
      id: 'sensors',
      title: 'Sensors',
      icon: '📊',
      options: [
        { id: 'piezometers', name: 'Piezometers' },
        { id: 'inclinometers', name: 'IPI/ Inclinometers' },
        { id: 'strain-gauges', name: 'Strain Gauges' },
        { id: 'accelerometers', name: 'Accelerometers' },
        { id: 'temperature', name: 'Temperature Sensors' },
        { id: 'tilt meters', name: 'Tilt Meters' },
        { id: 'load/ pressure cells', name: 'Load/ Pressure Cells' },
        { id: 'Crack meter/ Displacement Sensors', name: 'Crack Meter/ Displacement Sensors' },
        { id: 'Weather Station', name: 'Weather Station' },
        { id: 'Extensometers', name: 'Extensometers' }
      ],
      type: 'selection'
    },
    {
      id: 'iot',
      title: 'IoT & Communication',
      icon: '📡',
      options: [
        { id: 'RF lora', name: 'RF LoRa Nodes and Gateways' },
        { id: 'gsm', name: 'GSM/4G/5G Dataloggers' },
        { id: 'satellite', name: 'Satellite Transmission/ Starlink' },
        { id: 'High Speed Dynamic Dataloggers', name: 'High Speed Dynamic Dataloggers' }
      ],
      type: 'selection'
    },
    {
      id: 'remoteSensingSurveying',
      title: 'Remote Sensing & Surveying',
      icon: '🛰️',
      options: [
        { id: 'insar', name: 'InSAR' },
        { id: 'lidar', name: 'LIDAR' },
        { id: 'drone', name: 'Drone Surveys' },
        { id: 'optical', name: 'Hyperspectral/ Multispectral Imagery' },
        { id: 'Ground Penetrating Radar', name: 'Ground Penetrating Radar' },
        { id: 'total-stations', name: 'Total Stations' },
        { id: 'gnss', name: 'GNSS' },
        { id: 'laser-scanners', name: 'Laser Scanners' },
        { id: 'leveling', name: 'Leveling Instruments' }
      ],
      type: 'selection'
    },
    {
      id: 'dataManagement',
      title: 'Data Management',
      icon: '💾',
      options: [
        { id: 'cloud-based Data Management Platforms', name: 'Data Management Platforms' },
        { id: 'excel-logs', name: 'Manual Excel Logs' },
        { id: 'sql-databases', name: 'SQL Databases' },
        { id: 'custom-software', name: 'Custom Software' }
      ],
      type: 'selection'
    },
    {
      id: 'intelligence',
      title: 'Intelligence & Alerts',
      icon: '🧠',
      options: [
        { id: 'automated-reports', name: 'Automated Reports' },
        { id: 'real-time-alerts', name: 'Real-time Alerts' },
        { id: 'ai-ml-models', name: 'AI/ML Models' },
        { id: 'Virtual Dashboards', name: 'Dynamic Virtaul Dashboards' }
      ],
      type: 'selection'
    },
    {
      id: 'results',
      title: 'Your Integration Status',
      type: 'results'
    }
  ];

  useEffect(() => {
    if (isOpen) {
      // Check if assessment was already completed
      const assessmentCompleted = localStorage.getItem('assessmentCompleted');
      if (assessmentCompleted === 'true') {
        // Close modal immediately if already completed
        onClose();
        return;
      }

      setCurrentStep(0);
      setSelections({
        sensors: [],
        iot: [],
        remoteSensingSurveying: [],
        dataManagement: [],
        intelligence: []
      });
      setShowForm(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        designation: '',
        phone: '',
        countryCode: '+91'
      });
      setErrors({});
      setSubmitStatus(null);
    }
  }, [isOpen, onClose]);

  const handleOptionToggle = (stepId, optionId) => {
    setSelections(prev => ({
      ...prev,
      [stepId]: prev[stepId].includes(optionId)
        ? prev[stepId].filter(id => id !== optionId)
        : [...prev[stepId], optionId]
    }));
  };

  // Form handling functions
  const getPhoneLength = () => {
    const selectedCountry = countryCodes.find(c => c.code === formData.countryCode);
    return selectedCountry?.phoneLength || 10;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const maxLength = getPhoneLength();
      if (value.length > maxLength || (value && !/^[0-9]*$/.test(value))) {
        return;
      }
    }
    
    if (name === 'countryCode') {
      setFormData({
        ...formData,
        countryCode: value,
        phone: ''
      });
      if (errors.phone) {
        setErrors(prev => ({ ...prev, phone: '' }));
      }
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    } else if (formData.company.trim().length < 2) {
      newErrors.company = 'Company name must be at least 2 characters';
    }

    if (!formData.designation.trim()) {
      newErrors.designation = 'Designation is required';
    } else if (formData.designation.trim().length < 2) {
      newErrors.designation = 'Designation must be at least 2 characters';
    }

    const requiredPhoneLength = getPhoneLength();
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]+$/.test(formData.phone.trim())) {
      newErrors.phone = 'Phone number should only contain digits';
    } else if (formData.phone.trim().length !== requiredPhoneLength) {
      newErrors.phone = `Phone number must be exactly ${requiredPhoneLength} digits`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDownloadIntegrationReport = () => {
    console.log('📊 Generating Integration Report with user selections...');
    
    try {
      // Generate PDF once and reuse it - NO DELAY, INSTANT!
      console.log('💾 Generating PDF...');
      const doc = generateReportPDF();
      
      // Step 1: Download PDF to user's computer IMMEDIATELY
      const fileName = `integration-report-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      console.log(`✅ PDF downloaded: ${fileName}`);
      
      // Step 2: Send the same PDF via email as attachment (run in background)
      if (formData.email && formData.firstName && formData.lastName) {
        console.log('📧 Sending PDF via email to:', formData.email);
        // Pass the generated doc to avoid regenerating
        setTimeout(() => {
          sendReportViaEmailWithDoc(doc, formData.email, formData.firstName, formData.lastName).catch(err => {
            console.error('Email sending failed:', err);
          });
        }, 0);
      } else {
        console.warn('⚠️ Email not provided, skipping email send');
      }
      
      console.log('✅ Integration Report process completed!');
    } catch (error) {
      console.error('Error generating report:', error);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Show loader immediately
    setIsSubmitting(true);
    setSubmitStatus('generating');

    // Use setTimeout to allow UI to update with loader before PDF generation
    setTimeout(() => {
      try {
        // Generate and download PDF
        handleDownloadIntegrationReport();
        
        // Show success message
        setIsSubmitting(false);
        setSubmitStatus('success');
        
        // Mark assessment as completed in localStorage
        localStorage.setItem('assessmentCompleted', 'true');
        
        // Close modal after showing success message
        setTimeout(() => {
          onClose();
        }, 1500);

        // Submit to HubSpot in background (don't block UI)
        fetch(
          `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              fields: [
                { name: 'firstname', value: formData.firstName },
                { name: 'lastname', value: formData.lastName },
                { name: 'email', value: formData.email },
                { name: 'company', value: formData.company },
                { name: '0-2/designation', value: formData.designation },
                { name: 'phone', value: `${formData.countryCode} ${formData.phone}` }
              ],
              context: {
                pageUri: window.location.href,
                pageName: document.title
              }
            })
          }
        )
        .then(response => {
          if (response.ok) {
            console.log('✅ HubSpot submission successful');
          } else {
            console.warn('⚠️ HubSpot submission failed, but user data was saved');
          }
        })
        .catch(error => {
          console.error('❌ HubSpot submission error:', error);
        });
      } catch (error) {
        console.error('PDF generation error:', error);
        setIsSubmitting(false);
        setSubmitStatus('error');
      }
    }, 100); // Small delay to allow loader to show
  };

  const nextStep = () => {
    // Check if current step is sensors and requires selections
    const currentStepData = steps[currentStep];
    if (currentStepData.type === 'selection' && currentStepData.id === 'sensors') {
      const currentSelections = selections[currentStepData.id];
      if (!currentSelections || currentSelections.length === 0) {
        return; // Don't proceed if no selections made for sensors
      }
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      // If going back to intro (step 0), reset all selections
      if (currentStep === 1) {
        setSelections({
          sensors: [],
          iot: [],
          remoteSensingSurveying: [],
          dataManagement: [],
          intelligence: []
        });
      }
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceedToNext = () => {
    const currentStepData = steps[currentStep];
    // Only sensors step requires at least one selection
    if (currentStepData.type === 'selection' && currentStepData.id === 'sensors') {
      const currentSelections = selections[currentStepData.id];
      return currentSelections && currentSelections.length > 0;
    }
    return true; // All other steps can be skipped
  };

  const calculateIntegrationScore = () => {
    let score = 0;
    
    // Sensors - 20% (divide equally among all selections)
    const sensorsWeight = 20;
    const sensorsCount = steps.find(s => s.id === 'sensors')?.options.length || 1;
    const sensorsSelected = selections.sensors.length;
    score += (sensorsSelected / sensorsCount) * sensorsWeight;
    
    // IoT & Communication - 25% (divide equally among all selections)
    const iotWeight = 25;
    const iotCount = steps.find(s => s.id === 'iot')?.options.length || 1;
    const iotSelected = selections.iot.length;
    score += (iotSelected / iotCount) * iotWeight;
    
    // Remote Sensing & Surveying - 15%
    let remoteSensingScore = 0;
    const remoteSensingSelections = selections.remoteSensingSurveying;
    
    // InSAR - 5%
    if (remoteSensingSelections.includes('insar')) {
      remoteSensingScore += 5;
    }
    
    // LIDAR and Drone Surveys - 5% (combined)
    if (remoteSensingSelections.includes('lidar') || remoteSensingSelections.includes('drone')) {
      remoteSensingScore += 5;
    }
    
    // Rest of selections - 5% (divide equally)
    const otherRemoteSensing = remoteSensingSelections.filter(
      item => !['insar', 'lidar', 'drone'].includes(item)
    );
    const otherRemoteSensingCount = steps.find(s => s.id === 'remoteSensingSurveying')?.options.length - 3 || 1;
    remoteSensingScore += (otherRemoteSensing.length / otherRemoteSensingCount) * 5;
    
    score += Math.min(remoteSensingScore, 15); // Cap at 15%
    
    // Data Management - 30%
    let dataManagementScore = 0;
    const dataManagementSelections = selections.dataManagement;
    
    // Manual Excel Logs OR SQL Databases - 5% (combined)
    if (dataManagementSelections.includes('excel-logs') || dataManagementSelections.includes('sql-databases')) {
      dataManagementScore += 5;
    }
    
    // Data Management Platforms - 30%
    if (dataManagementSelections.includes('cloud-based Data Management Platforms')) {
      dataManagementScore += 30;
    }
    
    // Custom Software - 10%
    if (dataManagementSelections.includes('custom-software')) {
      dataManagementScore += 10;
    }
    
    score += Math.min(dataManagementScore, 30); // Cap at 30%
    
    // Intelligence & Alerts - 10% (divide equally among all selections)
    const intelligenceWeight = 10;
    const intelligenceCount = steps.find(s => s.id === 'intelligence')?.options.length || 1;
    const intelligenceSelected = selections.intelligence.length;
    score += (intelligenceSelected / intelligenceCount) * intelligenceWeight;
    
    return Math.round(score);
  };

  const calculateProgressPercentage = () => {
    // Progress based on current step, not final selections
    let progress = 0;
    
    // Intro step = 0%
    if (currentStep === 0) {
      return 0;
    }
    
    const stepOrder = ['sensors', 'iot', 'remoteSensingSurveying', 'dataManagement', 'intelligence'];
    
    // Calculate progress based on current position
    for (let i = 0; i < currentStep && i < stepOrder.length; i++) {
      const stepId = stepOrder[i];
      
      if (stepId === 'sensors') {
        // Sensors: 20% if at least one selection
        if (selections.sensors && selections.sensors.length > 0) {
          progress += 20;
        }
      } else if (stepId === 'iot') {
        // IoT: 25% if at least one selection
        if (selections.iot && selections.iot.length > 0) {
          progress += 25;
        }
      } else if (stepId === 'remoteSensingSurveying') {
        // Remote Sensing: Calculate based on specific selections
        let remoteSensingProgress = 0;
        const remoteSensingSelections = selections.remoteSensingSurveying || [];
        
        // InSAR: 5%
        if (remoteSensingSelections.includes('insar')) {
          remoteSensingProgress += 5;
        }
        
        // LIDAR or Drone Surveys: 5% (combined)
        if (remoteSensingSelections.includes('lidar') || remoteSensingSelections.includes('drone')) {
          remoteSensingProgress += 5;
        }
        
        // Rest of selections: 5% if any other is selected
        const otherSelections = remoteSensingSelections.filter(
          item => !['insar', 'lidar', 'drone'].includes(item)
        );
        if (otherSelections.length > 0) {
          remoteSensingProgress += 5;
        }
        
        progress += Math.min(remoteSensingProgress, 15); // Cap at 15%
      } else if (stepId === 'dataManagement') {
        // Data Management: Calculate based on specific selections
        let dataManagementProgress = 0;
        const dataManagementSelections = selections.dataManagement || [];
        
        // Manual Excel Logs OR SQL Databases: 5% (combined)
        if (dataManagementSelections.includes('excel-logs') || dataManagementSelections.includes('sql-databases')) {
          dataManagementProgress += 5;
        }
        
        // Data Management Platforms: 30%
        if (dataManagementSelections.includes('cloud-based Data Management Platforms')) {
          dataManagementProgress += 30;
        }
        
        // Custom Software: 10%
        if (dataManagementSelections.includes('custom-software')) {
          dataManagementProgress += 10;
        }
        
        progress += Math.min(dataManagementProgress, 30); // Cap at 30%
      } else if (stepId === 'intelligence') {
        // Intelligence: 10% if at least one selection
        if (selections.intelligence && selections.intelligence.length > 0) {
          progress += 10;
        }
      }
    }
    
    return progress;
  };

  const getIntegrationStatus = (score) => {
    if (score >= 90) {
      return { 
        status: 'Intelligence-Driven', 
        color: [16, 185, 129], // #10B981 - Green
        technicalMessage: "Your setup is advanced, using multi-sensor data fusion and trend analytics. You're ready to build digital twins and autonomous response systems.",
        marketingMessage: "You're a leader in integrated intelligence partner with Encardio to showcase innovation and co-develop next-gen monitoring intelligence."
      };
    }
    if (score >= 70) {
      return { 
        status: 'Integrated', 
        color: [34, 197, 94], // #22C55E - Green
        technicalMessage: "Your monitoring is mostly connected with automated data collection and visualization. The next step is intelligence automated reports, adaptable alerts and machine learning correlations.",
        marketingMessage: "Unlock decision intelligence by adding ML-driven insights and early warning automation. Encardio Rite's ecosystem helps you scale safely."
      };
    }
    if (score >= 40) {
      return { 
        status: 'Partially Integrated', 
        color: [245, 158, 11], // #F59E0B - Orange
        technicalMessage: "You have introduced some IoT or Software, but data still lives in separate tools. Near-real-time insights and data correlation analysis remain limited.",
        marketingMessage: "You're ready for the next phase consolidating multiple data sources into a unified decision layer with Proqio and IoT-enabled loggers."
      };
    }
    return { 
      status: 'Fragmented', 
      color: [239, 68, 68], // #EF4444 - Red
      technicalMessage: "Your systems are operating in silos with minimal connectivity between field sensors, data loggers, and data management. You rely heavily on manual processes.",
      marketingMessage: "Start with foundational digital integration — Encardio Rite helps unify your field data into a single platform through sensors, dataloggers, and Proqio."
    };
  };

  const generateReportPDF = () => {
    const score = calculateIntegrationScore();
    const status = getIntegrationStatus(score);
    
    // Create new PDF document with compression for faster generation
    const doc = new jsPDF({
      compress: true
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Logo is already imported as a data URL by webpack
    const logoBase64 = logoImage;
    
    // Set margins
    const leftMargin = 15;
    const rightMargin = 15;
    const topMargin = 20;
    const bottomMargin = 30;
    const contentWidth = pageWidth - leftMargin - rightMargin;
    
    // Colors
    const primaryColor = [34, 65, 221]; // #2241DD
    const darkColor = [31, 41, 55]; // #1f2937
    const lightGray = [107, 114, 128]; // #6b7280
    const veryLightGray = [156, 163, 175]; // #9ca3af
    
    // Helper function to add text with styling and word wrapping
    const addText = (text, x, y, options = {}) => {
      const {
        fontSize = 12,
        color = darkColor,
        fontStyle = 'normal',
        align = 'left',
        maxWidth = contentWidth
      } = options;
      
      doc.setFontSize(fontSize);
      doc.setTextColor(...color);
      doc.setFont('helvetica', fontStyle);
      
      // Split text into lines if it's too long
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, y, { align });
      
      return y + (lines.length * fontSize * 0.4); // Return new Y position
    };
    
    // Helper function to draw a line
    const drawLine = (x1, y1, x2, y2, color = lightGray, width = 0.5) => {
      doc.setDrawColor(...color);
      doc.setLineWidth(width);
      doc.line(x1, y1, x2, y2);
    };
    
    // Helper function to check if we need a new page
    const checkNewPage = (requiredSpace = 20) => {
      if (yPosition > pageHeight - bottomMargin - requiredSpace) {
        doc.addPage();
        return topMargin;
      }
      return yPosition;
    };
    
    // Header with Logo
    let yPosition = topMargin;
    
    // Left side - Company name and tagline
    addText('ENCARDIO RITE', leftMargin, yPosition, { fontSize: 16, fontStyle: 'bold', color: primaryColor });
    addText('METASENSING FOR MEGA STRUCTURES', leftMargin, yPosition + 6, { fontSize: 9, color: lightGray });
    
    // Right side - Logo (optimized for speed)
    try {
      const logoWidth = 45;
      const logoHeight = 12;
      const logoX = pageWidth - rightMargin - logoWidth;
      const logoY = topMargin;
      
      // Add logo with FAST compression for instant rendering
      doc.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight, 'logo', 'FAST');
    } catch (e) {
      // Skip logo if it fails - don't block PDF generation
      console.log('Logo skipped');
    }
    
    yPosition += 18;
    
    // Title
    yPosition = addText('Integration Assessment Report', leftMargin, yPosition, { fontSize: 18, fontStyle: 'bold', color: darkColor });
    
    // Date
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    yPosition = addText(`Generated on: ${currentDate}`, leftMargin, yPosition + 4, { fontSize: 8, color: lightGray });
    
    yPosition += 15;
    drawLine(leftMargin, yPosition, leftMargin + contentWidth, yPosition, veryLightGray, 0.5);
    yPosition += 12;
    
    // Integration Status Section with colored box
    yPosition = checkNewPage(70);
    
    // Add explanatory statement
    const integrationStatusIntro = "Integration Status: This report evaluates how integrated your monitoring ecosystem currently is- assessing data flow, communication, and decision intelligence across your systems.";
    const introLines = doc.splitTextToSize(integrationStatusIntro, contentWidth);
    
    doc.setFontSize(9);
    doc.setTextColor(...lightGray);
    doc.setFont('helvetica', 'italic');
    doc.text(introLines, leftMargin, yPosition);
    yPosition += introLines.length * 4 + 8;
    
    // Status header
    yPosition = addText('Integration Status', leftMargin, yPosition, { fontSize: 14, fontStyle: 'bold', color: darkColor });
    yPosition += 6;
    
    // Status box with background color
    const boxHeight = 20;
    const boxPadding = 8;
    
    // Draw colored background box
    doc.setFillColor(...status.color);
    doc.roundedRect(leftMargin, yPosition, contentWidth, boxHeight, 3, 3, 'F');
    
    // Status text (white text on colored background)
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text(`${status.status} (${score}%)`, leftMargin + boxPadding, yPosition + boxHeight/2 + 2);
    
    yPosition += boxHeight + 15;
    
    // Technical Message Section
    yPosition = checkNewPage(50);
    
    // Section with subtle background
    const technicalBoxY = yPosition;
    doc.setFillColor(248, 250, 252); // Very light blue-gray
    doc.roundedRect(leftMargin, technicalBoxY, contentWidth, 35, 2, 2, 'F');
    
    yPosition += 8;
    addText('Technical Assessment', leftMargin + 6, yPosition, { fontSize: 11, fontStyle: 'bold', color: primaryColor });
    yPosition += 6;
    
    // Technical message with word wrap
    const technicalLines = doc.splitTextToSize(status.technicalMessage, contentWidth - 12);
    doc.setFontSize(9);
    doc.setTextColor(...darkColor);
    doc.setFont('helvetica', 'normal');
    doc.text(technicalLines, leftMargin + 6, yPosition);
    yPosition += technicalLines.length * 3.5 + 10;
    
    // Marketing Message Section
    yPosition = checkNewPage(50);
    
    const marketingBoxY = yPosition;
    doc.setFillColor(252, 248, 250); // Very light purple-gray
    doc.roundedRect(leftMargin, marketingBoxY, contentWidth, 35, 2, 2, 'F');
    
    yPosition += 8;
    addText('Recommended Next Steps', leftMargin + 6, yPosition, { fontSize: 11, fontStyle: 'bold', color: primaryColor });
    yPosition += 6;
    
    // Marketing message with word wrap
    const marketingLines = doc.splitTextToSize(status.marketingMessage, contentWidth - 12);
    doc.setFontSize(9);
    doc.setTextColor(...darkColor);
    doc.setFont('helvetica', 'normal');
    doc.text(marketingLines, leftMargin + 6, yPosition);
    yPosition += marketingLines.length * 3.5 + 12;
    
    // Encardio Rite Ecosystem Section
    yPosition = checkNewPage(80);
    
    // Section separator line
    drawLine(leftMargin, yPosition, leftMargin + contentWidth, yPosition, veryLightGray, 0.5);
    yPosition += 10;
    
    // Section header with gradient-like styling
    yPosition = addText('Encardio Rite\'s Integration Ecosystem', leftMargin, yPosition, { 
      fontSize: 13, 
      fontStyle: 'bold', 
      color: primaryColor 
    });
    yPosition += 8;
    
    // Main paragraph with enhanced styling
    const ecosystemText = "There's never a dearth of data. What we struggle with is the information and intelligence most relevant to the decisions we need to make. That's where Infinitus 2.0 changes the game. It transforms raw data into insight, enables proactive decisions, and turns monitoring into a true performance advantage, drawing your attention to information that matters most for your project's safety and success.";
    
    // Create a subtle background box for the paragraph
    const paragraphLines = doc.splitTextToSize(ecosystemText, contentWidth - 16);
    const paragraphHeight = paragraphLines.length * 4 + 12;
    
    doc.setFillColor(250, 251, 255); // Very light blue background
    doc.roundedRect(leftMargin, yPosition - 2, contentWidth, paragraphHeight, 2, 2, 'F');
    
    // Add border to the box
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.3);
    doc.roundedRect(leftMargin, yPosition - 2, contentWidth, paragraphHeight, 2, 2, 'S');
    
    // Add the paragraph text
    yPosition += 6;
    doc.setFontSize(9);
    doc.setTextColor(...darkColor);
    doc.setFont('helvetica', 'normal');
    doc.text(paragraphLines, leftMargin + 8, yPosition);
    yPosition += paragraphLines.length * 4 + 10;
    
    // Section separator line
    drawLine(leftMargin, yPosition, leftMargin + contentWidth, yPosition, veryLightGray, 0.5);
    yPosition += 10;
    
    // What is Your Current State Section
    yPosition = checkNewPage(80);
    
    yPosition = addText('What is Your Current State', leftMargin, yPosition, { 
      fontSize: 14, 
      fontStyle: 'bold', 
      color: darkColor 
    });
    yPosition += 10;
    
    // Define the Infinitus Layer mapping
    const infinitusLayers = [
      {
        layer: 'Layer 1: Sensors (Earth)',
        categoryKey: 'sensors',
        getUserSelections: () => {
          const selected = selections.sensors || [];
          if (selected.length === 0) return null;
          const displayNames = selected.map(item => {
            const optionDisplayNames = {
              'piezometers': 'Piezometers',
              'inclinometers': 'IPI/Inclinometers',
              'strain-gauges': 'Strain Gauges',
              'accelerometers': 'Accelerometers',
              'temperature': 'Temperature Sensors',
              'tilt meters': 'Tilt Meters',
              'load/ pressure cells': 'Load/Pressure Cells',
              'Crack meter/ Displacement Sensors': 'Crack Meters',
              'Weather Station': 'Weather Station',
              'Extensometers': 'Extensometers'
            };
            return optionDisplayNames[item] || item;
          });
          return displayNames.join(', ');
        },
        meaning: 'You\'ve established the foundation of physical data collection.',
        infinitus: 'Encardio Rite\'s high-precision sensors, remote sensing technologies, and structural instruments build the backbone of intelligent data.'
      },
      {
        layer: 'Layer 2: IoT & Connectivity (Field-Edge)',
        categoryKey: 'iot',
        getUserSelections: () => {
          const selected = selections.iot || [];
          if (selected.length === 0) return null;
          const displayNames = selected.map(item => {
            const optionDisplayNames = {
              'RF lora': 'LoRa',
              'gsm': 'GSM/4G/5G',
              'satellite': 'Satellite',
              'High Speed Dynamic Dataloggers': 'Dynamic Dataloggers'
            };
            return optionDisplayNames[item] || item;
          });
          return displayNames.join(', ');
        },
        meaning: 'Your instruments are starting to communicate, but integration depth defines efficiency.',
        infinitus: 'Dataloggers at the edge ecosystem enable modular, low-power, multi-protocol connectivity across projects.'
      },
      {
        layer: 'Layer 3: Remote Sensing & Surveying (Sky)',
        categoryKey: 'remoteSensingSurveying',
        getUserSelections: () => {
          const selected = selections.remoteSensingSurveying || [];
          if (selected.length === 0) return null;
          const displayNames = selected.map(item => {
            const optionDisplayNames = {
              'insar': 'InSAR',
              'lidar': 'LiDAR',
              'drone': 'Drones',
              'optical': 'Hyperspectral/Multispectral',
              'Ground Penetrating Radar': 'GPR',
              'total-stations': 'Total Stations',
              'gnss': 'GNSS',
              'laser-scanners': 'Laser Scanners',
              'leveling': 'Leveling Instruments'
            };
            return optionDisplayNames[item] || item;
          });
          return displayNames.join(', ');
        },
        meaning: 'You\'re augmenting ground truth with aerial and satellite insight.',
        infinitus: 'Infinitus fuses spatial and ground data into unified geospatial intelligence.'
      },
      {
        layer: 'Layer 4: Data Management (Cloud)',
        categoryKey: 'dataManagement',
        getUserSelections: () => {
          const selected = selections.dataManagement || [];
          if (selected.length === 0) return null;
          const displayNames = selected.map(item => {
            const optionDisplayNames = {
              'cloud-based Data Management Platforms': 'Cloud Platforms',
              'excel-logs': 'Excel',
              'sql-databases': 'Databases',
              'custom-software': 'Custom Tools'
            };
            return optionDisplayNames[item] || item;
          });
          return displayNames.join(', ');
        },
        meaning: 'Data exists, but accessibility and automation define scalability.',
        infinitus: 'Proqio Cloud centralizes, validates, and automates analytics and reporting pipelines.'
      },
      {
        layer: 'Layer 5: Intelligence & Alerts (Cognition)',
        categoryKey: 'intelligence',
        getUserSelections: () => {
          const selected = selections.intelligence || [];
          if (selected.length === 0) return null;
          const displayNames = selected.map(item => {
            const optionDisplayNames = {
              'automated-reports': 'Reports',
              'real-time-alerts': 'Alerts',
              'ai-ml-models': 'AI/ML Models',
              'Virtual Dashboards': 'Dashboards'
            };
            return optionDisplayNames[item] || item;
          });
          return displayNames.join(', ');
        },
        meaning: 'You\'ve begun translating data into insight, the frontier of Decision Intelligence.',
        infinitus: 'Infinitus AI Layer correlates trends, detects anomalies, and drives decision intelligence systems.'
      }
    ];
    
    // Filter layers where user has made selections
    const activeLayers = infinitusLayers.filter(layer => {
      const userSelections = layer.getUserSelections();
      return userSelections !== null;
    });
    
    if (activeLayers.length > 0) {
      // Draw table for each active layer
      activeLayers.forEach((layer, index) => {
        yPosition = checkNewPage(50);
        
        const userSelections = layer.getUserSelections();
        
        // Layer header with colored background
        const layerColors = [
          [139, 92, 246], // Purple for Layer 1
          [59, 130, 246],  // Blue for Layer 2
          [16, 185, 129],  // Green for Layer 3
          [245, 158, 11],  // Orange for Layer 4
          [239, 68, 68]    // Red for Layer 5
        ];
        const layerColor = layerColors[index % layerColors.length];
        
        // Draw layer header box
        doc.setFillColor(...layerColor);
        doc.roundedRect(leftMargin, yPosition, contentWidth, 8, 1, 1, 'F');
        
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.text(layer.layer, leftMargin + 4, yPosition + 5.5);
        
        yPosition += 10;
        
        // Calculate content height first to draw background
        const boxStartY = yPosition;
        
        // Calculate heights
        const selectionLines = doc.splitTextToSize(userSelections, contentWidth - 10);
        const meaningLines = doc.splitTextToSize(layer.meaning, contentWidth - 10);
        const infinitusLines = doc.splitTextToSize(layer.infinitus, contentWidth - 10);
        
        const contentHeight = 4 + 4 + selectionLines.length * 3 + 2 + 4 + meaningLines.length * 3 + 3 + 4 + infinitusLines.length * 3 + 2;
        
        // Draw background box first
        doc.setFillColor(249, 250, 251);
        doc.roundedRect(leftMargin, boxStartY, contentWidth, contentHeight, 1, 1, 'F');
        
        // Draw border
        doc.setDrawColor(...layerColor);
        doc.setLineWidth(0.5);
        doc.roundedRect(leftMargin, boxStartY, contentWidth, contentHeight, 1, 1, 'S');
        
        // Now add the text content on top
        yPosition += 4;
        
        // What the User Selected
        doc.setFontSize(8);
        doc.setTextColor(...darkColor);
        doc.setFont('helvetica', 'bold');
        doc.text('What You Selected:', leftMargin + 4, yPosition);
        yPosition += 4;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.text(selectionLines, leftMargin + 4, yPosition);
        yPosition += selectionLines.length * 3 + 2;
        
        // Meaning of Current State
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Meaning of Your Current State:', leftMargin + 4, yPosition);
        yPosition += 4;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.text(meaningLines, leftMargin + 4, yPosition);
        yPosition += meaningLines.length * 3 + 3;
        
        // What Infinitus Represents
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryColor);
        doc.text('What Infinitus Represents:', leftMargin + 4, yPosition);
        yPosition += 4;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(...darkColor);
        doc.text(infinitusLines, leftMargin + 4, yPosition);
        yPosition += infinitusLines.length * 3 + 2;
        
        yPosition += 5;
      });
    } else {
      yPosition = addText('No selections made in any layer.', leftMargin, yPosition, { 
        fontSize: 9, 
        color: lightGray, 
        fontStyle: 'italic' 
      });
      yPosition += 10;
    }
    
    yPosition += 5;
    
    // Integration Opportunities & Value Additions Section
    yPosition = checkNewPage(80);
    
    // Section separator line
    drawLine(leftMargin, yPosition, leftMargin + contentWidth, yPosition, veryLightGray, 0.5);
    yPosition += 10;
    
    yPosition = addText('Integration Opportunities & Value Additions', leftMargin, yPosition, { 
      fontSize: 14, 
      fontStyle: 'bold', 
      color: darkColor 
    });
    yPosition += 6;
    
    addText('Unlock additional value by expanding your monitoring ecosystem:', leftMargin, yPosition, { 
      fontSize: 9, 
      color: lightGray,
      fontStyle: 'italic'
    });
    yPosition += 10;
    
    // Define missing layer opportunities
    const missingLayerOpportunities = [
      {
        layer: 'Sensors (Earth Layer)',
        categoryKey: 'sensors',
        strategicExplanation: 'While you\'ve established a foundation, expanding into holistic sensing—covering geotechnical (piezometers, extensometers), structural (strain gauges, tiltmeters, accelerometers), environmental (rainfall, temperature), and seismic (Strong Motion Accelerometers)—enhances visibility across the entire asset lifecycle.',
        infinitusAdvantage: 'Infinitus Earth Layer integrates Encardio Rite\'s comprehensive suite of instruments to deliver 360° insight from sub-surface to superstructure, ensuring safety and data continuity.'
      },
      {
        layer: 'IoT & Communication (Field-Edge Layer)',
        categoryKey: 'iot',
        strategicExplanation: 'Without dataloggers and communication gateways, valuable data remains isolated. Real-time transmission, redundancy, and edge processing are crucial for timely insight and automation.',
        infinitusAdvantage: 'Our IoT ecosystem provides modular dataloggers and gateways with LoRa, LTE, Satellite, and intelligent edge computing for seamless field-to-cloud data flow.'
      },
      {
        layer: 'Remote Sensing & Surveying (Sky Layer)',
        categoryKey: 'remoteSensingSurveying',
        strategicExplanation: 'Without remote sensing (InSAR, LiDAR, drone, optical) or surveying (GNSS, total stations), you miss macro-level movement patterns that complement ground truth.',
        infinitusAdvantage: 'Infinitus fuses satellite and terrestrial measurements, creating a unified geospatial intelligence model within Proqio.'
      },
      {
        layer: 'Data Management (Cloud Layer)',
        categoryKey: 'dataManagement',
        strategicExplanation: 'Without centralized data integration, analytics remain fragmented. Consolidation across hardware, databases, and remote sensing is key for operational intelligence.',
        infinitusAdvantage: 'Proqio Platform integrates sensor data, InSAR, environmental, and survey inputs in one secure dashboard, enabling visualization, reports, and APIs for collaboration.'
      },
      {
        layer: 'Intelligence & Alerts (Cognition Layer)',
        categoryKey: 'intelligence',
        strategicExplanation: 'Without automation, insights remain reactive. Automated reporting, trend correlation, and outlier detection unlock predictive monitoring and decision intelligence.',
        infinitusAdvantage: 'Infinitus identifies patterns, anomalies, and risks through automated reports, ML-based trends, and correlation engines, powering early warning systems.'
      }
    ];
    
    // Filter layers where user has NOT made selections
    const missingLayers = missingLayerOpportunities.filter(layer => {
      const selectedItems = selections[layer.categoryKey] || [];
      return selectedItems.length === 0;
    });
    
    if (missingLayers.length > 0) {
      // Draw opportunity cards for each missing layer
      missingLayers.forEach((layer, index) => {
        yPosition = checkNewPage(60);
        
        // Layer colors (muted/lighter versions for missing layers)
        const opportunityColors = [
          [196, 181, 253], // Light Purple for Sensors
          [147, 197, 253],  // Light Blue for IoT
          [134, 239, 172],  // Light Green for Remote Sensing
          [253, 186, 116],  // Light Orange for Data Management
          [252, 165, 165]   // Light Red for Intelligence
        ];
        
        const allLayers = ['sensors', 'iot', 'remoteSensingSurveying', 'dataManagement', 'intelligence'];
        const layerIndex = allLayers.indexOf(layer.categoryKey);
        const layerColor = opportunityColors[layerIndex];
        
        // Draw layer header box with gradient-like effect
        doc.setFillColor(...layerColor);
        doc.roundedRect(leftMargin, yPosition, contentWidth, 8, 1, 1, 'F');
        
        doc.setFontSize(10);
        doc.setTextColor(...darkColor);
        doc.setFont('helvetica', 'bold');
        doc.text(layer.layer, leftMargin + 4, yPosition + 5.5);
        
        yPosition += 10;
        
        // Calculate heights first
        const boxStartY = yPosition;
        const explanationLines = doc.splitTextToSize(layer.strategicExplanation, contentWidth - 10);
        const advantageLines = doc.splitTextToSize(layer.infinitusAdvantage, contentWidth - 14);
        const advantageBoxHeight = advantageLines.length * 3 + 4;
        
        const totalContentHeight = 4 + 4 + explanationLines.length * 3 + 3 + 3 + advantageBoxHeight + 4;
        
        // Draw white background box first
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(leftMargin, boxStartY, contentWidth, totalContentHeight, 1, 1, 'F');
        
        // Draw border with dashed style for opportunity
        doc.setDrawColor(...layerColor);
        doc.setLineWidth(0.5);
        doc.setLineDash([2, 2]);
        doc.roundedRect(leftMargin, boxStartY, contentWidth, totalContentHeight, 1, 1, 'S');
        doc.setLineDash([]); // Reset dash
        
        // Now add content on top
        yPosition += 4;
        
        // Strategic Explanation
        doc.setFontSize(8);
        doc.setTextColor(...darkColor);
        doc.setFont('helvetica', 'bold');
        doc.text('Strategic Explanation:', leftMargin + 4, yPosition);
        yPosition += 4;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.text(explanationLines, leftMargin + 4, yPosition);
        yPosition += explanationLines.length * 3 + 3;
        
        // Infinitus Advantage (highlighted)
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryColor);
        doc.text('Infinitus Advantage:', leftMargin + 4, yPosition);
        yPosition += 3;
        
        // Light blue background for advantage
        doc.setFillColor(240, 249, 255);
        doc.roundedRect(leftMargin + 4, yPosition, contentWidth - 8, advantageBoxHeight, 1, 1, 'F');
        
        // Add advantage text
        yPosition += 4;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(...darkColor);
        doc.text(advantageLines, leftMargin + 6, yPosition);
        yPosition += advantageLines.length * 3 + 4;
        
        yPosition += 5;
      });
    } else {
      yPosition = addText('Great! You\'re leveraging all key integration layers.', leftMargin, yPosition, { 
        fontSize: 9, 
        color: [16, 185, 129], // Green
        fontStyle: 'bold' 
      });
      yPosition += 10;
    }
    
    yPosition += 10;
    
    // Let's Build Your Integrated Future Section
    // Start on a new page for better layout
    doc.addPage();
    yPosition = topMargin + 10;
    
    // Main heading
    yPosition = addText('Let\'s Build Your Integrated Future', leftMargin + (contentWidth / 2), yPosition, { 
      fontSize: 16, 
      fontStyle: 'bold', 
      color: primaryColor,
      align: 'center',
      maxWidth: contentWidth
    });
    yPosition += 12;
    
    // Content - Technical progression
    const introText = "Integration is a technical progression across the monitoring workflow.";
    
    // Define the progression stages
    const progressionStages = [
      {
        title: "Data Acquisition",
        description: "Instruments, sensors, remote sensing, and geospatial inputs continuously capture raw field data."
      },
      {
        title: "Integration",
        description: "Dataloggers, communication systems, and cloud platforms standardize, synchronize, and unify data streams into a single monitoring environment."
      },
      {
        title: "Analysis",
        description: "Dashboards, reports, and analytical models convert unified data into performance indicators, correlations, and system behavior insights."
      },
      {
        title: "Early Warning Systems",
        description: "Thresholds, alerts, and automated response logic enable real-time risk detection and operational decision support."
      }
    ];
    
    const conclusionText1 = "As each layer is connected, monitoring evolves from isolated data points to a continuous, decision intelligence loop.";
    const conclusionText2 = "This is the transition from data to decision intelligence.";
    
    // Pre-calculate all text lines for accurate height
    const closingIntroLines = doc.splitTextToSize(introText, contentWidth - 20);
    const conclusion1Lines = doc.splitTextToSize(conclusionText1, contentWidth - 20);
    const conclusion2Lines = doc.splitTextToSize(conclusionText2, contentWidth - 20);
    
    // Calculate stages text
    const stageTextData = progressionStages.map(stage => ({
      title: stage.title,
      descLines: doc.splitTextToSize(stage.description, contentWidth - 26)
    }));
    
    // Calculate total height needed with proper spacing
    let totalHeight = closingIntroLines.length * 4 + 10; // Intro text
    stageTextData.forEach(stage => {
      totalHeight += 6 + stage.descLines.length * 3.8 + 4; // Title + description + spacing
    });
    totalHeight += 6 + conclusion1Lines.length * 4 + 10; // Conclusion 1 + spacing
    totalHeight += conclusion2Lines.length * 5 + 16; // Conclusion 2 + padding
    
    // Create a gradient-style background box
    const closingBoxY = yPosition;
    
    // Draw background with subtle gradient effect (using light to lighter blue)
    doc.setFillColor(245, 247, 255); // Very light blue
    doc.roundedRect(leftMargin, closingBoxY, contentWidth, totalHeight, 3, 3, 'F');
    
    // Add subtle border
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.8);
    doc.roundedRect(leftMargin, closingBoxY, contentWidth, totalHeight, 3, 3, 'S');
    
    // Add intro text
    yPosition += 8;
    doc.setFontSize(9.5);
    doc.setTextColor(...darkColor);
    doc.setFont('helvetica', 'normal');
    doc.text(closingIntroLines, leftMargin + 10, yPosition);
    yPosition += closingIntroLines.length * 4 + 10;
    
    // Add progression stages
    stageTextData.forEach((stage) => {
      // Stage title
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...primaryColor);
      doc.text(`${stage.title} –`, leftMargin + 10, yPosition);
      yPosition += 6;
      
      // Stage description
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...darkColor);
      doc.text(stage.descLines, leftMargin + 10, yPosition);
      yPosition += stage.descLines.length * 3.8 + 4;
    });
    
    yPosition += 6;
    
    // Add conclusion text 1
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkColor);
    doc.text(conclusion1Lines, leftMargin + 10, yPosition);
    yPosition += conclusion1Lines.length * 4 + 10;
    
    // Add conclusion text 2 (emphasized)
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    const closingCenterX = leftMargin + (contentWidth / 2);
    doc.text(conclusion2Lines, closingCenterX, yPosition, { align: 'center' });
    yPosition += conclusion2Lines.length * 5 + 8;
    
    yPosition += 20;
    
    // Footer
    yPosition = pageHeight - bottomMargin;
    drawLine(leftMargin, yPosition, leftMargin + contentWidth, yPosition, lightGray);
    yPosition += 8;
    
    // Center the footer text properly
    const footerText1 = 'For more information, visit our website or contact our team';
    const footerText2 = 'www.encardio.com';
    
    // Calculate center position for text
    const centerX = leftMargin + (contentWidth / 2);
    
    addText(footerText1, centerX, yPosition, { 
      fontSize: 8, 
      color: lightGray, 
      align: 'center',
      maxWidth: contentWidth
    });
    addText(footerText2, centerX, yPosition + 5, { 
      fontSize: 8, 
      color: primaryColor, 
      align: 'center',
      maxWidth: contentWidth
    });
    
    return doc;
  };

  const sendReportViaEmailWithDoc = async (doc, email, firstName, lastName) => {
    try {
      // Check if EmailJS credentials are configured
      if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
        console.warn('⚠️ EmailJS credentials not configured. Please check your .env file.');
        console.warn('Required variables: REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID, REACT_APP_EMAILJS_PUBLIC_KEY');
        return; // Skip email sending if credentials are not configured
      }

      const score = calculateIntegrationScore();
      const integrationStatus = getIntegrationStatus(score);
      
      // Log user selections being included in the PDF
      const totalSelectedItems = Object.values(selections).flat().length;
      console.log(`📋 PDF includes ${totalSelectedItems} selected items:`);
      console.log('  - Sensors:', selections.sensors.length, 'items');
      console.log('  - IoT & Communication:', selections.iot.length, 'items');
      console.log('  - Remote Sensing & Surveying:', selections.remoteSensingSurveying.length, 'items');
      console.log('  - Data Management:', selections.dataManagement.length, 'items');
      console.log('  - Intelligence & Alerts:', selections.intelligence.length, 'items');
      console.log(`  - Integration Score: ${score}% (${integrationStatus.status})`);
      
      // Use the already generated PDF doc (no need to regenerate)
      console.log('📎 Using pre-generated PDF for email attachment...');
      
      // Get PDF as base64 string (EmailJS preferred format for attachments)
      const pdfBase64 = doc.output('datauristring');
      const base64Content = pdfBase64.split(',')[1]; // Remove the data:application/pdf;base64, prefix
      
      const fileName = `Integration-Report-${new Date().toISOString().split('T')[0]}.pdf`;
      const pdfSizeKB = Math.round((base64Content.length * 0.75) / 1024); // Approximate size in KB
      
      console.log(`📎 PDF generated: ${fileName} (${pdfSizeKB} KB)`);
      
      // Format user selections for email body
      const formatSelections = () => {
        const optionDisplayNames = {
          // Sensors
          'piezometers': 'Piezometers',
          'inclinometers': 'IPI/ Inclinometers',
          'strain-gauges': 'Strain Gauges',
          'accelerometers': 'Accelerometers',
          'temperature': 'Temperature Sensors',
          'tilt meters': 'Tilt Meters',
          'load/ pressure cells': 'Load/ Pressure Cells',
          'Crack meter/ Displacement Sensors': 'Crack Meter/ Displacement Sensors',
          'Weather Station': 'Weather Station',
          'Extensometers': 'Extensometers',
          // IoT & Communication
          'RF lora': 'RF LoRa Nodes and Gateways',
          'gsm': 'GSM/4G/5G Dataloggers',
          'satellite': 'Satellite Transmission/ Starlink',
          'High Speed Dynamic Dataloggers': 'High Speed Dynamic Dataloggers',
          // Remote Sensing
          'insar': 'InSAR',
          'lidar': 'LIDAR',
          'drone': 'Drone Surveys',
          'optical': 'Hyperspectral/ Multispectral Imagery',
          'Ground Penetrating Radar': 'Ground Penetrating Radar',
          'total-stations': 'Total Stations',
          'gnss': 'GNSS',
          'laser-scanners': 'Laser Scanners',
          'leveling': 'Leveling Instruments',
          // Data Management
          'cloud-based Data Management Platforms': 'Data Management Platforms',
          'excel-logs': 'Manual Excel Logs',
          'sql-databases': 'SQL Databases',
          'custom-software': 'Custom Software',
          // Intelligence & Alerts
          'automated-reports': 'Automated Reports',
          'real-time-alerts': 'Real-time Alerts',
          'ai-ml-models': 'AI/ML Models',
          'Virtual Dashboards': 'Dynamic Virtual Dashboards'
        };

        let selectionText = '';
        const categories = [
          { key: 'sensors', name: 'Sensors' },
          { key: 'iot', name: 'IoT & Communication' },
          { key: 'remoteSensingSurveying', name: 'Remote Sensing & Surveying' },
          { key: 'dataManagement', name: 'Data Management' },
          { key: 'intelligence', name: 'Intelligence & Alerts' }
        ];

        categories.forEach(category => {
          const selectedItems = selections[category.key];
          if (selectedItems && selectedItems.length > 0) {
            selectionText += `\n${category.name}:\n`;
            selectedItems.forEach(item => {
              const displayName = optionDisplayNames[item] || item.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              selectionText += `• ${displayName}\n`;
            });
          }
        });

        return selectionText || 'No selections made';
      };

      const userSelections = formatSelections();
      const totalSelections = Object.values(selections).flat().length;
      
      // Prepare email parameters with all required fields
      const templateParams = {
        to_email: email,
        to_name: `${firstName} ${lastName}`,
        firstName: firstName,
        lastName: lastName,
        from_name: 'Encardio Rite',
        reply_to: 'mishravishal9151@gmail.com',
        company: formData.company,
        designation: formData.designation,
        phone: `${formData.countryCode} ${formData.phone}`,
        score: score,
        status: integrationStatus.status,
        reportDate: new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        title: 'Integration Assessment Report',
        totalSelections: totalSelections,
        userSelections: userSelections,
        
      };

      console.log('📤 Sending email with attachment via EmailJS...');
      
      // Send email using EmailJS with attachment
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log('✅ Confirmation email sent successfully to:', email);
      console.log('📧 Check your inbox for the report details!');
    } catch (error) {
      console.error('❌ Error sending report via email:', error);
      console.error('Please verify your EmailJS credentials and template configuration');
      // Don't throw error - still allow form submission to succeed
    }
  };

  if (!isOpen) return null;

  const currentStepData = steps[currentStep];
  const isIntroStep = currentStepData.type === 'intro';
  const isResultsStep = currentStepData.type === 'results';
  const score = calculateIntegrationScore();
  const integrationStatus = getIntegrationStatus(score);

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
        </div>

        <div className="modal-content">
          {isIntroStep && (
            <div className="intro-step">
              <h2 className="intro-title">{currentStepData.title}</h2>
              <p className="intro-description">{currentStepData.description}</p>
            </div>
          )}

          {currentStepData.type === 'selection' && (
            <div className="selection-step">
              <div className="step-header">
                <span className="step-icon">{currentStepData.icon}</span>
                <h3 className="step-title">{currentStepData.title}</h3>
              </div>
              <div className="options-grid">
                {currentStepData.options.map(option => (
                  <div
                    key={option.id}
                    className={`option-card ${selections[currentStepData.id].includes(option.id) ? 'selected' : ''}`}
                    onClick={() => handleOptionToggle(currentStepData.id, option.id)}
                  >
                    {selections[currentStepData.id].includes(option.id) && (
                      <div className="checkmark">✓</div>
                    )}
                    <span className="option-name">{option.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isResultsStep && !showForm && (
            <div className="results-step">
              <h2 className="results-title">{currentStepData.title}</h2>
              <div className="progress-container">
                <div className="progress-bar-large">
                  <div 
                    className="progress-fill-large" 
                    style={{ width: `${score}%` }}
                  />
                </div>
                <div 
                  className="integration-status"
                  style={{ color: `rgb(${integrationStatus.color.join(',')})` }}
                >
                  {integrationStatus.status} ({score}%)
                </div>
              </div>
              <p className="results-description">
                {integrationStatus.technicalMessage}
              </p>
              <button className="consult-btn" onClick={() => setShowForm(true)}>
                Get Your Report
              </button>
            </div>
          )}

          {isResultsStep && showForm && (
            <div className="form-step">
              {/* <h2 className="form-step-title">Gain more from the Infrastructure Monitoring you're already paying for.</h2> */}
              <form onSubmit={handleFormSubmit} className="assessment-form">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">First Name*</label>
                    <input
                      type="text"
                      name="firstName"
                      className={`form-input ${errors.firstName ? 'error' : ''}`}
                      value={formData.firstName}
                      onChange={handleFormChange}
                      disabled={isSubmitting}
                    />
                    {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Last Name*</label>
                    <input
                      type="text"
                      name="lastName"
                      className={`form-input ${errors.lastName ? 'error' : ''}`}
                      value={formData.lastName}
                      onChange={handleFormChange}
                      disabled={isSubmitting}
                    />
                    {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Official E-mail*</label>
                  <input
                    type="email"
                    name="email"
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    value={formData.email}
                    onChange={handleFormChange}
                    disabled={isSubmitting}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Company Name*</label>
                    <input
                      type="text"
                      name="company"
                      className={`form-input ${errors.company ? 'error' : ''}`}
                      value={formData.company}
                      onChange={handleFormChange}
                      disabled={isSubmitting}
                    />
                    {errors.company && <span className="error-message">{errors.company}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Designation*</label>
                    <input
                      type="text"
                      name="designation"
                      className={`form-input ${errors.designation ? 'error' : ''}`}
                      value={formData.designation}
                      onChange={handleFormChange}
                      disabled={isSubmitting}
                    />
                    {errors.designation && <span className="error-message">{errors.designation}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Phone No.*</label>
                  <div className="phone-input-container">
                    <select
                      name="countryCode"
                      className="country-code-selector"
                      value={formData.countryCode}
                      onChange={handleFormChange}
                      disabled={isSubmitting}
                    >
                      {countryCodes.map((country, index) => (
                        <option key={index} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      name="phone"
                      className={`phone-input ${errors.phone ? 'error' : ''}`}
                      value={formData.phone}
                      onChange={handleFormChange}
                      placeholder={`Enter ${getPhoneLength()} digit number`}
                      maxLength={getPhoneLength()}
                      disabled={isSubmitting}
                    />
                  </div>
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>

                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <span className="button-loading">
                      <span className="spinner"></span>
                      Generating PDF...
                    </span>
                  ) : (
                    'Download Now'
                  )}
                </button>

                {submitStatus === 'generating' && (
                  <div className="generating-message">
                    <span className="loader-spinner"></span>
                    Generating your report, please wait...
                  </div>
                )}

                {submitStatus === 'success' && (
                  <div className="success-message">
                    ✓ Thank you! Your integration report has been downloaded successfully.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="error-message-general">
                    Sorry, there was an error. Please try again.
                  </div>
                )}
              </form>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {/* Progress bar visible on all steps except results */}
          {!isResultsStep && (
            <div className="progress-section">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${calculateProgressPercentage()}%` }}
                />
              </div>
              <div className="progress-text">
                {Math.round(calculateProgressPercentage())}%
              </div>
            </div>
          )}
          
          <div className="nav-buttons">
            {!isIntroStep && !isResultsStep && (
              <button 
                className="nav-btn prev-btn" 
                onClick={prevStep}
                disabled={currentStep === 0}
              >
                Previous
              </button>
            )}
            
            {!isResultsStep && (
              <button 
                className={`nav-btn next-btn ${!canProceedToNext() ? 'disabled' : ''}`}
                onClick={nextStep}
                disabled={!canProceedToNext()}
              >
                {isIntroStep ? 'Start Assessment' : 'Next'}
              </button>
            )}
            
            {!isIntroStep && !isResultsStep && !canProceedToNext() && (
              <div className="validation-message">
                Please select at least one option to continue
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationAssessmentModal;
